import { useAdminText } from './shared/useAdminText';
import { AICostPeriodFilter } from './shared/AICostPeriodFilter';
import { readCostPeriod, costDateRange, costPeriodParams, isValidCostPeriod } from '../utils/aiCostPeriod';
import { AdminTable } from './shared/AdminDataGrid';
/**
 * AI Cost List Component
 * Displays paginated list of users with AI cost summaries
 */

import { useState, useEffect } from 'react';
import { Title } from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  LinearProgress,
  Chip,
  Alert,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Link } from '@mui/material';

import { adminApi } from '../providers/dataProvider';
import { formatTokens } from '../utils/formatters';
import { presentRecordedCost } from '../utils/aiCostPresentation';
import { AICostCalls } from './AICostCalls';
import { useAICostQuery } from '../hooks/useAICostQuery';

type SortField = 'totalCost' | 'totalTokens' | 'queryCount' | 'email';

export function AICostList() {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'users' ? 'users' : 'calls';
  return <Box>
    <Title title="AI Costs" />
    <Tabs value={view} onChange={(_, value) => { const next = new URLSearchParams(params); if (value === 'users') next.set('view', 'users'); else next.delete('view'); setParams(next); }} aria-label="AI cost views" sx={{ mb: 2 }}>
      <Tab value="calls" label="All calls" /><Tab value="users" label="By user" />
    </Tabs>
    <AICostPeriodFilter key={`period:${params}`} />
    {view === 'calls' ? <AICostCalls key={params.toString()} /> : <AICostUsers key={params.toString()} />}
  </Box>;
}

function AICostUsers() {
  const [params] = useSearchParams();
  const t = useAdminText();
  const period = readCostPeriod(params);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sort, setSort] = useState<SortField>('totalCost');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const query = useAICostQuery(
    ['ai-costs', 'users', page, rowsPerPage, sort, order, searchDebounced, period],
    () => adminApi.getAICostUsers(page * rowsPerPage, (page + 1) * rowsPerPage, sort, order, searchDebounced || undefined, costDateRange(period)), isValidCostPeriod(period),
  );
  const data = query.data?.data;
  const total = query.data?.total;
  const loading = query.isFetching;

  const handleSort = (field: SortField) => {
    if (sort === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(field);
      setOrder('desc');
    }
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box px={2} pb={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="body2">User totals exclude system / unattributed calls. See All calls for the complete ledger.</Typography>
          <Button onClick={() => void query.refetch()} disabled={loading}>Refresh</Button>
        </Box>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>
          {query.accessDenied ? 'Access to AI costs was denied. Reauthenticate before retrying.' : data ? 'Could not refresh costs. Showing the last successful result.' : 'Could not load costs.'}
        </Alert>}
        {/* Search Bar */}
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <TextField
              fullWidth
              label="Search users"
              size="small"
              placeholder="Search by email or name..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </CardContent>
        </Card>

        {/* Data Table */}
        <TableContainer component={Paper}>
          {loading && <LinearProgress />}
          <AdminTable>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sort === 'email'}
                    direction={sort === 'email' ? order : 'asc'}
                    onClick={() => handleSort('email')}
                  >
                    User
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sort === 'totalCost'}
                    direction={sort === 'totalCost' ? order : 'desc'}
                    onClick={() => handleSort('totalCost')}
                  >
                    Recorded Cost
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sort === 'totalTokens'}
                    direction={sort === 'totalTokens' ? order : 'desc'}
                    onClick={() => handleSort('totalTokens')}
                  >
                    Recorded Tokens
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sort === 'queryCount'}
                    direction={sort === 'queryCount' ? order : 'desc'}
                    onClick={() => handleSort('queryCount')}
                  >
                    Calls
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                      {searchDebounced ? 'No users found matching your search' : 'No AI cost data available'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {data?.map((user) => (
                <TableRow key={user.userId} hover>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/ai-costs/${user.userId}?${costPeriodParams(params)}`}
                      underline="hover"
                    >
                      {user.email}
                    </Link>
                    {user.name && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        {user.name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={presentRecordedCost(user.totalCost, user.costCoverage, t).value}
                      size="small"
                      color={user.totalCost > 0.1 ? 'warning' : 'default'}
                      variant="outlined"
                    />
                    <Typography variant="caption" display="block" color="textSecondary">
                      {presentRecordedCost(user.totalCost, user.costCoverage, t).detail}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatTokens(user.totalTokens)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {user.queryCount}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </AdminTable>
          {total !== undefined && <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />}
        </TableContainer>
      </Box>
    </Box>
  );
}
